// Every fragment below reads `theme.colors.*` and `theme.visualPrimitives.*`,
// which exist only because of the `declare module '@mui/material/styles'`
// blocks in `./theme`. Without this type-only side-effect import those
// properties are absent from any TypeScript program that does not happen to
// also include `theme.ts` — exactly the scoped compile `gchat/CLAUDE.md`
// mandates as the type gate.
import type {} from './theme';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/**
 * Shared visual-language sx-fragment primitives. Every value is read from
 * `theme.visualPrimitives.*` or `theme.colors.*` — no raw hex, no inline
 * RGBA colour string, and no bare px string duplicating a token lives in
 * this module. Each fragment is a theme-callback, uniformly typed
 * `(theme: Theme) => SystemStyleObject<Theme>` (never a plain object, never
 * the looser `SxProps<Theme>`) so every export is uniformly spy-able and
 * uniformly assertable — a getter-form spy throws under this repo's
 * Babel→CJS output, so the function form is mandatory, with no exceptions.
 *
 * Consumers compose these via MUI's array-form `sx`, e.g.
 * `sx={[row, focusRing, pressable]}`.
 */

/**
 * `.eng-item` box + hairline (flow-chrome.css:50-51). De-chromes MUI's own
 * hover/focus-visible background overlays — this fragment is paired at the
 * consumption site with `disableRipple`/`disableGutters` so nothing else
 * can reintroduce the ripple or gutter chrome it removes here. Does not set
 * `display`: in this DOM the title and meta line are siblings inside
 * `ListItemText`, not inside the button itself — see `rowText`.
 */
export const row = (theme: Theme): SystemStyleObject<Theme> => ({
  width: '100%',
  textAlign: 'left',
  padding: `${theme.visualPrimitives.rowPaddingY} 0`,
  borderTop: `1px solid ${theme.visualPrimitives.border}`,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
  },
});

/**
 * `.eng-item`'s grid rhythm (flow-chrome.css:52), applied to `ListItemText`'s
 * root — the title and meta line actually live there, not inside `row`'s
 * `ListItemButton`. Also zeroes `ListItemText`'s own default vertical
 * margin.
 */
export const rowText = (theme: Theme): SystemStyleObject<Theme> => ({
  my: 0,
  display: 'grid',
  gap: theme.visualPrimitives.rowGap,
});

/**
 * `.eng-item .t` (flow-chrome.css:54) plus its hover shift to `--accent-deep`
 * (flow-chrome.css:56). The hover selector targets a PARENT
 * (`ListItemButton`) hovering a CHILD (this title) — encoded as
 * `'.MuiListItemButton-root:hover &'` so the guarantee travels with the
 * primitive; a plain `&:hover` on the title itself would only fire when the
 * pointer is directly over the text, which the prototype does not do.
 */
export const rowTitle = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.titleSize,
  fontWeight: theme.visualPrimitives.titleWeight,
  letterSpacing: theme.visualPrimitives.titleTracking,
  color: theme.colors.inkPrimary,
  '.MuiListItemButton-root:hover &': {
    color: theme.colors.lavenderDeep,
  },
});

/**
 * `.eng-item.ghost .t` (flow-chrome.css:54, :57-58) — the CTA row's quieter
 * title treatment: same size/tracking as `rowTitle`, weight 400, ink-3 base,
 * shifting to ink-2 on the same parent-hover selector.
 */
export const rowTitleQuiet = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.titleSize,
  fontWeight: 400,
  letterSpacing: theme.visualPrimitives.titleTracking,
  color: theme.colors.inkTertiary,
  '.MuiListItemButton-root:hover &': {
    color: theme.colors.inkSecondary,
  },
});

/**
 * `.eng-item .m` (flow-chrome.css:55) — the single meta ink shared by
 * status, separator and timestamp alike, replacing an earlier two-tone
 * `text.secondary` + `text.disabled` split.
 */
export const meta = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.metaSize,
  lineHeight: theme.visualPrimitives.metaLineHeight,
  color: theme.colors.inkTertiary,
});

/**
 * The prototype's own focus convention (flow-chrome.css:29-31) — an
 * outside-offset accent ring on `:focus-visible` itself, not MUI's
 * `.Mui-focusVisible` class. Supersedes an earlier shipped inset ring.
 *
 * Ring colour resolves to `theme.colors.lavenderDeep` (`--accent-deep`), not
 * the prototype's own `--accent` (`theme.colors.lavender`, `tokens.css:53`)
 * that `flow-chrome.css:30` names. Deliberate divergence:
 * `theme.colors.lavender`'s hex (`theme.ts`'s `colors` const) measures
 * 2.94:1 against `theme.palette.background.default` (white, the surface
 * `pageSurface` paints), below WCAG 2.1 SC 1.4.11's 3:1 non-text floor —
 * and `row`'s compensating `'&.Mui-focusVisible'` background tint was
 * removed, so this outline is the sole focus affordance with no fallback
 * cue. `theme.colors.lavenderDeep`'s hex measures 4.15:1, clearing the
 * floor. Enforced by an arithmetic contrast assertion in
 * `visualLanguage.test.ts`, not by inspection.
 *
 * Deliberately sets no `borderRadius`. In the prototype's plain CSS,
 * `.btn { border-radius: 999px }` and this `:focus-visible` rule share equal
 * specificity, so the later `.btn` rule wins and the pill keeps its shape.
 * MUI merges an `sx` array into ONE emotion class, which inverts that — the
 * `:focus-visible`-scoped declaration would out-specify the base-level one
 * and square the pill for the duration of keyboard focus. A consumer that
 * genuinely wants a square-cornered ring should apply
 * `theme.visualPrimitives.focusRingRadius` at its own call site.
 */
export const focusRing = (theme: Theme): SystemStyleObject<Theme> => ({
  '&:focus-visible': {
    outline: `${theme.visualPrimitives.focusRingWidth} solid ${theme.colors.lavenderDeep}`,
    outlineOffset: theme.visualPrimitives.focusRingOffset,
  },
});

/**
 * `.btn`'s press feedback (flow-chrome.css:12, :14; tokens.css:96-97).
 *
 * PITFALL: the 'prefers-reduced-motion' override MUST be nested INSIDE
 * '&:active', not placed as its sibling. A sibling-level override only
 * resets the un-pseudo-scoped `transform`; `.foo:active` still outranks a
 * sibling `.foo` in the cascade, so the press animation keeps firing for
 * reduce-motion users even though the object shape looks type-legal either
 * way. No automated gate in this repo catches a sibling-level regression
 * here — a manual side-by-side visual check is the only one. Write it
 * correctly the first time.
 */
export const pressable = (theme: Theme): SystemStyleObject<Theme> => ({
  transition: `opacity ${theme.visualPrimitives.tFast} ${theme.visualPrimitives.ease}, transform ${theme.visualPrimitives.tFast} ${theme.visualPrimitives.ease}`,
  '&:active': {
    transform: theme.visualPrimitives.pressScale,
    '@media (prefers-reduced-motion: reduce)': {
      transform: 'none',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

/**
 * The route-scoped white backdrop (tokens.css:35 `--bg`). Reads
 * `theme.palette.background.default`, which already resolves to the
 * prototype's white — no new colour token is needed, only a per-route `sx`
 * override. `.main-content`'s global sand (its `background-color` rule
 * inside the `MuiCssBaseline` `styleOverrides`) is deliberately NOT changed
 * here: this fragment overrides it locally on advisory routes only, so chat
 * keeps sand.
 */
export const pageSurface = (theme: Theme): SystemStyleObject<Theme> => ({
  bgcolor: theme.palette.background.default,
  minHeight: theme.visualPrimitives.pageMinHeight,
});

/**
 * `.eng-list .inner`'s content column (flow-chrome.css:47) — the shared
 * 560px column every advisory surface (list, detail, and the callout states
 * rendered inside them) is constrained to.
 *
 * Expressed relative to its own container (`width: 100%` + `maxWidth:
 * theme.visualPrimitives.columnMaxWidth`) rather than the prototype's
 * viewport-relative `min(560px, 92vw)`, because both advisory consumers
 * already sit inside containers that carry their own page padding — that
 * padding supplies the narrow-viewport gutter the prototype's `92vw` half
 * was for. Below roughly 400px, `92vw` made the column wider than the list
 * page's `100vw - 32px` content box; per CSS 2.1 §10.3.3 an over-constrained
 * block resolves its `auto` margins to `0`, so the column pinned left
 * instead of centring. On the engagement page the same over-wide child was
 * instead silently shrunk by `flex-shrink`, so the two routes disagreed
 * about what the same token meant at the same viewport.
 */
export const contentColumn = (theme: Theme): SystemStyleObject<Theme> => ({
  width: '100%',
  maxWidth: theme.visualPrimitives.columnMaxWidth,
  mx: 'auto',
});

/**
 * The shared heading role for every advisory screen title — a DELIBERATE
 * DIVERGENCE from the prototype. The prototype renders this slot as an
 * eyebrow (`flow-app.js:101`, `<span class="eyebrow h">`), but `/advisory`
 * lives inside gchat's Header/Footer shell rather than the prototype's full-screen
 * overlay, where a 12px all-caps label would read as a section tag rather
 * than the page's own identity. Resolves to `h6`'s own themed weight
 * (`theme.typography.h6.fontWeight`) — `variant="h6"` stays on every
 * consumer for semantics and document outline; only the local
 * `fontWeight: 600` override it used to carry is removed.
 */
export const heading = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.headingSize,
  fontWeight: theme.visualPrimitives.headingWeight,
  color: theme.colors.inkPrimary,
});

/**
 * `.btn`'s pill shape (flow-chrome.css:10-11; tokens.css:83) — action button
 * geometry. Its only current consumer is `AdvisoryCalloutState`'s "Try
 * again" button; composes with `pressable`/`focusRing` for motion and focus.
 */
export const pillAction = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.actionSize,
  fontWeight: theme.visualPrimitives.actionWeight,
  padding: theme.visualPrimitives.actionPadding,
  borderRadius: theme.visualPrimitives.radiusPill,
});

/**
 * `.eyebrow` (brief/styles.css:35-39) — encoded with NO CONSUMER yet. This
 * is deliberate: this role is needed throughout (`.wiz-eyebrow`, `.cl-h`,
 * `.gs-h`, `.ct-h` in flow-chrome.css, the wizard step counter and the
 * criteria/key-question numerals). Do not delete this export as dead code —
 * it is a first-class primitive that simply has no advisory-screen consumer
 * yet.
 */
export const eyebrow = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'block',
  fontSize: theme.visualPrimitives.eyebrowSize,
  fontWeight: theme.visualPrimitives.eyebrowWeight,
  letterSpacing: theme.visualPrimitives.eyebrowTracking,
  textTransform: 'uppercase',
  color: theme.colors.inkTertiary,
});

/**
 * `.num, .mono` (brief/styles.css:32; tokens.css:80) — encoded with NO
 * CONSUMER yet. This role is needed by a step counter (`.wiz-top .step`)
 * and numerals (`.cl-row .n`, `.kq-head .n`) not yet built. Uses the system
 * fallback chain only — `theme.visualPrimitives.monoStack` deliberately
 * excludes JetBrains Mono (self-hosting it is explicitly deferred). Do not
 * delete this export as dead code.
 */
export const mono = (theme: Theme): SystemStyleObject<Theme> => ({
  fontFamily: theme.visualPrimitives.monoStack,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.01em',
});

/**
 * `.cl-row .t` (flow-chrome.css:180) — the item-text column shared by
 * criteria and anti-goal rows alike; the anti-goal variant overrides only
 * `color` at its own consumption site (flow-chrome.css:181), never here.
 *
 * Currently unconsumed: its only production consumer, `AdvisoryCriteriaEditor`,
 * was deleted. Kept anyway — this repo ships fragments ahead of their
 * consumers when the prototype value is already known (`eyebrow` did the
 * same for a whole phase). Its shape is still worth pinning even with no
 * call site.
 */
export const listItemText = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.clTextSize,
  lineHeight: theme.visualPrimitives.clTextLineHeight,
  color: theme.colors.inkPrimary,
});

/**
 * `.rm` / `.rm:hover` (flow-chrome.css:182-183) — the quiet remove control
 * shared by every removable row. The hover state resolves to
 * `theme.colors.vermilion`, standing in for the prototype's own
 * removal-hover custom property: that property's literal value exists
 * nowhere in `theme.ts`'s `colors` const, and introducing it fresh would
 * violate this module's own "no raw hex" rule. This is a deliberate
 * divergence.
 */
export const removeControl = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.visualPrimitives.clRemoveSize,
  color: theme.colors.inkTertiary,
  whiteSpace: 'nowrap',
  minWidth: 0,
  padding: 0,
  textTransform: 'none',
  '&:hover': {
    color: theme.colors.vermilion,
    backgroundColor: 'transparent',
  },
});

/**
 * `.kq-card` (flow-chrome.css:187-205) — a single key-question card's
 * hairline separator and inner padding. Reads `theme.visualPrimitives.border`
 * (the heavier rule) deliberately, not `borderLight` — the prototype
 * distinguishes the rule BETWEEN cards from the lighter one between
 * sub-questions (`subQuestionRowGrid`, below), and that weight difference is
 * a deliberate prototype choice, not an inconsistency to normalise.
 */
export const keyQuestionCard = (theme: Theme): SystemStyleObject<Theme> => ({
  borderTop: `1px solid ${theme.visualPrimitives.border}`,
  padding: theme.visualPrimitives.kqCardPadding,
});

/**
 * `.kq-head` (flow-chrome.css:187-205) — the key-question row's grid rhythm:
 * a 52px first column to hold the `KQ n` marker, the question text, and a
 * trailing remove control. Reads `theme.visualPrimitives.kqRowColumns` and
 * `kqRowGap`.
 */
export const keyQuestionRowGrid = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'grid',
  gridTemplateColumns: theme.visualPrimitives.kqRowColumns,
  gap: theme.visualPrimitives.kqRowGap,
  alignItems: 'baseline',
});

/**
 * `.kq-sub` (flow-chrome.css:187-205) — the sub-question row: the same grid
 * rhythm as `keyQuestionRowGrid` plus its own vertical padding and a bottom
 * hairline reading `theme.visualPrimitives.borderLight` — the lighter rule,
 * deliberately distinct from `keyQuestionCard`'s heavier `border` (see that
 * fragment's own doc comment).
 */
export const subQuestionRowGrid = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'grid',
  gridTemplateColumns: theme.visualPrimitives.kqRowColumns,
  gap: theme.visualPrimitives.kqRowGap,
  alignItems: 'baseline',
  padding: `${theme.visualPrimitives.kqSubPaddingY} 0`,
  borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
});

/**
 * `.dropzone` (flow-chrome.css:219-224) — the inert evidence panel. Carries
 * no hover or drag-over accent treatment at all (flow-chrome.css:225) and
 * no pointer cursor — there is nothing to hover or click, so nothing here
 * needs a `prefers-reduced-motion` guard either (`pressable`'s guard exists
 * only because `pressable` has a transition; this fragment has none). The
 * border is SOLID: the prototype's own rule reads
 * `border: 1px solid var(--border)` — the prototype's broken-line
 * convention some retellings assume is not what its CSS actually declares,
 * and this fragment does not invent one. `radiusCard` is this token's first
 * consumer.
 */
export const evidenceDropzone = (theme: Theme): SystemStyleObject<Theme> => ({
  marginTop: theme.visualPrimitives.dzMarginTop,
  border: `1px solid ${theme.visualPrimitives.border}`,
  borderRadius: theme.visualPrimitives.radiusCard,
  padding: theme.visualPrimitives.dzPadding,
  textAlign: 'center',
});
